import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BottomNav from '@/components/BottomNav';
import SuiteChatPanel from '@/components/SuiteChatPanel';
import PresencePanel from '@/components/PresencePanel';
import MediaCalendarWidget from '@/components/MediaCalendarWidget';
import {
  Users, ShoppingBag, BookOpen, Megaphone, Calendar, MapPin, DollarSign,
  Camera, CheckCircle2, Upload, Youtube, FileText, MessageSquare, Sparkles, Trash2, X, Plus,
  TrendingUp, ShieldCheck, Edit2, Image as ImageIcon, BarChart3,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const TABS = [
  { key: 'meet', icon: Users, id: 'Buddhist Meet', en: 'Buddhist Meet', desc_id: 'Buat & daftar acara komunitas', desc_en: 'Create & register for events' },
  { key: 'chat', icon: MessageSquare, id: 'Buddhist Chat', en: 'Buddhist Chat', desc_id: 'Grup wilayah — chat, foto, video, polling', desc_en: 'Regional groups — chat, media, polls' },
  { key: 'mart', icon: ShoppingBag, id: 'Buddhist Mart', en: 'Buddhist Mart', desc_id: 'Jual-beli & lowongan kerja', desc_en: 'Marketplace & job listings' },
  { key: 'mastery', icon: BookOpen, id: 'Buddhist Mastery', en: 'Buddhist Mastery', desc_id: 'E-book & forum Q&A', desc_en: 'Resource library & Q&A' },
  { key: 'media', icon: Megaphone, id: 'Buddhist Media', en: 'Buddhist Media', desc_id: 'Paste broadcast → analisa AI', desc_en: 'Paste broadcast → AI analysis' },
];

const EVENT_CATEGORIES = ['Kebaktian', 'Puja Bakti', 'Meditasi', 'Dharma Talk', 'Baksos', 'Donor Darah', 'Waisak', 'Kathina', 'Asadha', 'Retreat', 'Pelatihan', 'Lainnya'];
const MART_CATEGORIES = ['Umum', 'Barang', 'Elektronik', 'Fashion', 'Rumah Tangga', 'Kendaraan', 'Mobil', 'Motor', 'Sepeda', 'Properti', 'Rumah', 'Apartemen', 'Tanah', 'Buku', 'Makanan', 'Kuliner', 'Kesehatan', 'Kecantikan', 'Olahraga', 'Hobi', 'Jasa', 'Lainnya'];

const rp = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const incentiveFor = (n) => {
  if (n < 1) return 0;
  if (n <= 5) return 5000;
  if (n <= 10) return 6000;
  if (n <= 20) return 7000;
  if (n <= 30) return 8000;
  if (n <= 40) return 9000;
  return 10000;
};

export default function BuddhistSuitePage() {
  const { API, token, member, claimedProfile } = useAuth();
  const { lang } = useLang();
  const [tab, setTab] = useState('meet');
  const isBuddhist = claimedProfile?.category === 'Buddhist Match';
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  if (!isBuddhist) {
    return (
      <div className="min-h-screen t-bg flex items-center justify-center px-6">
        <div className="glass-card rounded-3xl p-8 max-w-md text-center">
          <Sparkles className="w-12 h-12 text-[#D98A94] mx-auto mb-4" />
          <h2 className="text-xl font-bold t-text mb-2">
            {lang === 'en' ? 'Buddhist Suite' : 'Buddhist Suite'}
          </h2>
          <p className="t-text-secondary text-sm">
            {lang === 'en'
              ? 'This feature is exclusive to members of the Buddhist Match category. Please claim a Buddhist Match profile first.'
              : 'Fitur ini eksklusif untuk anggota kategori Buddhist Match. Silakan klaim profil Buddhist Match terlebih dahulu.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen t-bg pb-24">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-3">
              <Sparkles className="w-3.5 h-3.5 text-[#D98A94]" />
              <span className="text-xs t-text-secondary font-medium">Buddhist Match Exclusive</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold t-text font-romantic">Buddhist Suite</h1>
          </div>
          <Link
            to="/kategori/buddhist-match"
            data-testid="suite-stats-buddhist"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#D98A94]/30 bg-[#D98A94]/10 hover:bg-[#D98A94]/20 text-[#C0727C] text-xs font-bold transition-colors"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            <span>{lang === 'en' ? 'Deep Stats' : 'Statistik Komunitas'}</span>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
          {TABS.map(tb => {
            const isActive = tab === tb.key;
            return (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                data-testid={`suite-tab-${tb.key}`}
                className={`p-3 sm:p-4 rounded-2xl border transition-all text-left ${isActive ? 'bg-[#D98A94] text-white border-[#D98A94] shadow-lg shadow-[#D98A94]/30' : 'glass-card t-text hover:border-[#D98A94]/50'}`}
              >
                <tb.icon className={`w-5 h-5 mb-1.5 ${isActive ? 'text-white' : 'text-[#D98A94]'}`} />
                <div className="text-xs sm:text-sm font-bold">{lang === 'en' ? tb.en : tb.id}</div>
                <div className={`text-[10px] mt-1 ${isActive ? 'text-white/85' : 't-text-muted'}`}>{lang === 'en' ? tb.desc_en : tb.desc_id}</div>
              </button>
            );
          })}
        </div>

        {tab === 'meet' && <MeetSection API={API} authHeader={authHeader} lang={lang} member={member} />}
        {tab === 'chat' && (
          <div>
            <PresencePanel authHeader={() => authHeader} lang={lang} />
            <div className="h-4" />
            <SuiteChatPanel API={API} scope="buddhist" token={token} accountId={member?.id} lang={lang} />
          </div>
        )}
        {tab === 'mart' && <MartSection API={API} authHeader={authHeader} lang={lang} member={member} />}
        {tab === 'mastery' && <MasterySection API={API} token={token} authHeader={authHeader} accountId={member?.id} lang={lang} />}
        {tab === 'media' && <MediaSection API={API} token={token} authHeader={authHeader} accountId={member?.id} lang={lang} />}
      </div>
      <BottomNav active="buddhist" />
    </div>
  );
}

// ============ MEET ============
export function MeetSection({ API, authHeader, lang, member, scope = 'buddhist' }) {
  const [meets, setMeets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailMeet, setDetailMeet] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${scope}/meets`, { headers: authHeader });
      setMeets(data.meets || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  }, [API, authHeader, scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PresencePanel authHeader={() => authHeader} lang={lang} />
      <div className="h-4" />
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold t-text">{lang === 'en' ? 'Community Events' : 'Acara Komunitas'}</h2>
        <Button onClick={() => setCreateOpen(true)} data-testid="meet-create-btn" className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full text-sm">
          <Plus className="w-4 h-4 mr-1" /> {lang === 'en' ? 'Create Event' : 'Buat Acara'}
        </Button>
      </div>

      {/* Insentif Berjenjang — premium tiered card */}
      <div className="relative rounded-2xl p-5 mb-5 overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF7F5 0%, #FFE9EB 55%, #F9DEE1 100%)', border: '1px solid rgba(217,138,148,0.35)' }}>
        {/* Decorative glow */}
        <div aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30 blur-2xl" style={{ background: '#D98A94' }} />
        <div aria-hidden className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full opacity-20 blur-2xl" style={{ background: '#F59E0B' }} />

        <div className="relative flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 backdrop-blur-sm border border-[#D98A94]/25 mb-2">
              <Sparkles className="w-3 h-3 text-[#D98A94]" />
              <span className="text-[10px] font-bold text-[#D98A94] uppercase tracking-widest">
                {lang === 'en' ? 'Reward Program' : 'Program Reward'}
              </span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold t-text leading-tight">
              {lang === 'en' ? 'Tiered Incentive' : 'Insentif Berjenjang'}
            </h3>
            <p className="text-xs t-text-secondary mt-0.5">
              {lang === 'en'
                ? 'Reward per person × total participants. The bigger the crowd, the higher the rate.'
                : 'Reward per orang × jumlah peserta. Makin ramai, tarif per orang naik.'}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-[#D98A94]/20">
            <TrendingUp className="w-3.5 h-3.5 text-[#D98A94]" />
            <span className="text-[11px] font-semibold t-text">
              {lang === 'en' ? 'Up to Rp10rb / person' : 'Hingga Rp10rb / orang'}
            </span>
          </div>
        </div>

        {/* Tier grid */}
        <div className="relative grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          {[
            { r: '1–5',   v: 'Rp5rb',  hi: false },
            { r: '6–10',  v: 'Rp6rb',  hi: false },
            { r: '11–20', v: 'Rp7rb',  hi: false },
            { r: '21–30', v: 'Rp8rb',  hi: false },
            { r: '31–40', v: 'Rp9rb',  hi: false },
            { r: '41+',   v: 'Rp10rb', hi: true  },
          ].map((tier) => (
            <div
              key={tier.r}
              className={`relative rounded-xl px-2 py-2.5 text-center transition-all ${tier.hi
                ? 'bg-gradient-to-br from-[#D98A94] to-[#C7867A] text-white shadow-md shadow-[#D98A94]/30 border border-[#D98A94]'
                : 'bg-white/85 backdrop-blur-sm border border-[#D98A94]/15'}`}
            >
              {tier.hi && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#F59E0B] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow uppercase tracking-wide">Max</span>
              )}
              <div className={`text-[9px] font-bold uppercase tracking-wider ${tier.hi ? 'text-white/85' : 't-text-muted'}`}>
                {lang === 'en' ? 'People' : 'Peserta'}
              </div>
              <div className={`text-[11px] font-bold ${tier.hi ? 'text-white' : 't-text'}`}>{tier.r}</div>
              <div className={`mt-1 pt-1 border-t ${tier.hi ? 'border-white/25 text-white' : 'border-[#D98A94]/15 t-text'} text-sm font-extrabold`}>{tier.v}</div>
              <div className={`text-[8.5px] font-semibold ${tier.hi ? 'text-white/85' : 't-text-muted'}`}>
                / {lang === 'en' ? 'person' : 'orang'}
              </div>
            </div>
          ))}
        </div>

        {/* Worked examples */}
        <div className="relative mb-3 rounded-lg p-3 bg-white/70 backdrop-blur-sm border border-[#D98A94]/15">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#D98A94] mb-2 flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" />
            {lang === 'en' ? 'How it works — example' : 'Cara kerja — contoh perhitungan'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="text-[11px] t-text-secondary bg-white/80 rounded-md px-2.5 py-2 border border-[#D98A94]/10">
              <span className="t-text font-bold">11 orang</span> × <span className="t-text font-bold">Rp7.000</span> = <span className="text-[#C7867A] font-extrabold">Rp77.000</span>
            </div>
            <div className="text-[11px] t-text-secondary bg-white/80 rounded-md px-2.5 py-2 border border-[#D98A94]/10">
              <span className="t-text font-bold">25 orang</span> × <span className="t-text font-bold">Rp8.000</span> = <span className="text-[#C7867A] font-extrabold">Rp200.000</span>
            </div>
            <div className="text-[11px] t-text-secondary bg-white/85 rounded-md px-2.5 py-2 border border-[#D98A94]/25">
              <span className="t-text font-bold">50 orang</span> × <span className="t-text font-bold">Rp10.000</span> = <span className="text-[#D98A94] font-extrabold">Rp500.000</span>
            </div>
          </div>
          <div className="text-[10px] t-text-muted mt-2 italic">
            {lang === 'en'
              ? 'Tier rate is applied to the total participant count (including the initiator).'
              : 'Tarif tier dikalikan ke total peserta acara (termasuk diri sendiri sebagai inisiator).'}
          </div>
        </div>

        <div className="relative flex items-start gap-2 text-[11px] t-text-secondary bg-white/60 backdrop-blur-sm rounded-lg px-3 py-2 border border-[#D98A94]/15">
          <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
          <span>
            {lang === 'en'
              ? 'Paid manually via WhatsApp to the bank account you entered, after admin verifies your timestamped proof photo.'
              : 'Dibayar admin via WhatsApp ke rekening bank yang Anda daftarkan, setelah foto bukti (dengan timestamp) diverifikasi.'}
          </span>
        </div>
      </div>

      {loading ? <div className="t-text-muted text-sm text-center py-8">Loading...</div> : meets.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center">
          <Calendar className="w-10 h-10 text-[#D98A94] mx-auto mb-3" />
          <p className="t-text-muted text-sm">{lang === 'en' ? 'No events yet — be the first to create one!' : 'Belum ada acara — jadilah yang pertama membuat!'}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {meets.map(m => (
            <button key={m.id} onClick={() => setDetailMeet(m)} data-testid={`meet-card-${m.id}`}
              className="glass-card rounded-2xl p-4 text-left hover:border-[#D98A94]/40 transition">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold t-text text-sm">{m.title}</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D98A94]/15 text-[#D98A94]">{m.event_category}</span>
              </div>
              <div className="flex items-center gap-1 text-xs t-text-muted mb-1"><Calendar className="w-3 h-3" />{m.event_date}{m.event_time_start ? ` · ${m.event_time_start}${m.event_time_end ? `–${m.event_time_end}` : ''}` : ''}</div>
              <div className="flex items-center gap-1 text-xs t-text-muted mb-1"><MapPin className="w-3 h-3" />{m.location}</div>
              <div className="flex items-center gap-1 text-xs t-text-secondary mt-2"><Users className="w-3 h-3 text-[#D98A94]" />{m.participants?.length || 0} / {m.max_participants}</div>
              {m.status === 'paid' && <div className="text-[10px] text-green-600 mt-1">✓ {lang === 'en' ? 'Incentive paid' : 'Insentif dibayar'}</div>}
              {m.status === 'completed' && !m.incentive_paid && <div className="text-[10px] text-amber-600 mt-1">⏱ {lang === 'en' ? 'Awaiting payment' : 'Menunggu pembayaran'}</div>}
            </button>
          ))}
        </div>
      )}

      {createOpen && <MeetCreateModal API={API} authHeader={authHeader} lang={lang} scope={scope} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {detailMeet && <MeetDetailModal API={API} authHeader={authHeader} lang={lang} scope={scope} meet={detailMeet} member={member} onClose={() => setDetailMeet(null)} onChange={() => { load(); setDetailMeet(null); }} />}
    </div>
  );
}

function MeetCreateModal({ API, authHeader, lang, scope = 'buddhist', onClose, onCreated }) {
  const [f, setF] = useState({ title: '', description: '', location: '', maps_url: '', event_date: '', event_time_start: '', event_time_end: '', event_category: 'Kebaktian', max_participants: 50, bank_name: '', bank_account_holder: '', bank_account_number: '' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.title || !f.location || !f.event_date) return toast.error(lang === 'en' ? 'Please fill all required fields' : 'Isi semua kolom wajib');
    if (!f.event_time_start || !f.event_time_end) {
      return toast.error(lang === 'en' ? 'Start & end time are required' : 'Jam mulai & jam selesai wajib diisi');
    }
    if (f.event_time_end <= f.event_time_start) {
      return toast.error(lang === 'en' ? 'End time must be after start time' : 'Jam selesai harus setelah jam mulai');
    }
    if (!f.bank_name.trim() || !f.bank_account_holder.trim() || !f.bank_account_number.trim()) {
      return toast.error(lang === 'en'
        ? 'Bank name, account holder name, and account number are required for incentive payout'
        : 'Nama bank, nama pemilik rekening, dan nomor rekening wajib diisi untuk pencairan insentif');
    }
    setLoading(true);
    try {
      await axios.post(`${API}/${scope}/meets`, f, { headers: authHeader });
      toast.success(lang === 'en' ? 'Event created' : 'Acara dibuat');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-card t-text max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{lang === 'en' ? 'Create Event' : 'Buat Acara'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <Input placeholder={lang === 'en' ? 'Meetup Event Name *' : 'Nama Acara Kopdar *'} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="meet-input-title" />
          <Textarea placeholder={lang === 'en' ? 'Description' : 'Deskripsi'} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="meet-input-desc" />
          <Input placeholder={lang === 'en' ? 'Location *' : 'Lokasi *'} value={f.location} onChange={e => setF({ ...f, location: e.target.value })} data-testid="meet-input-location" />
          <Input placeholder={lang === 'en' ? 'Google Maps URL (optional)' : 'Link Google Maps (opsional)'} value={f.maps_url} onChange={e => setF({ ...f, maps_url: e.target.value })} data-testid="meet-input-maps" />
          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{lang === 'en' ? 'Event Date' : 'Tanggal Acara'} *</label>
            <Input type="date" value={f.event_date} onChange={e => setF({ ...f, event_date: e.target.value })} data-testid="meet-input-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{lang === 'en' ? 'Start Time' : 'Jam Mulai'} *</label>
              <Input type="time" value={f.event_time_start} onChange={e => setF({ ...f, event_time_start: e.target.value })} data-testid="meet-input-time-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{lang === 'en' ? 'End Time' : 'Jam Selesai'} *</label>
              <Input type="time" value={f.event_time_end} onChange={e => setF({ ...f, event_time_end: e.target.value })} data-testid="meet-input-time-end" />
            </div>
          </div>
          <select value={f.event_category} onChange={e => setF({ ...f, event_category: e.target.value })} className="w-full h-10 rounded-lg t-border border px-3 t-text bg-transparent" data-testid="meet-input-category">
            {EVENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{lang === 'en' ? 'Estimated Participants' : 'Estimasi Peserta'}</label>
            <Input type="number" min={1} max={500} placeholder={lang === 'en' ? 'Estimated Participants' : 'Estimasi Peserta'} value={f.max_participants} onChange={e => setF({ ...f, max_participants: Number(e.target.value) })} data-testid="meet-input-max" />
          </div>

          {/* Bank details for incentive payout — mandatory */}
          <div className="rounded-xl p-3" style={{ background: 'rgba(217,138,148,0.06)', border: '1px solid rgba(217,138,148,0.25)' }}>
            <div className="flex items-center gap-1.5 mb-2">
              <DollarSign className="w-3.5 h-3.5 text-[#D98A94]" />
              <label className="text-[10px] uppercase tracking-wide font-bold text-[#D98A94]">
                {lang === 'en' ? 'Bank Account for Incentive Payout' : 'Rekening Bank untuk Pencairan Insentif'}
              </label>
            </div>
            <p className="text-[10px] t-text-muted mb-2 leading-snug">
              {lang === 'en'
                ? 'Admin will transfer the incentive to this account after verifying your timestamped proof photo.'
                : 'Admin akan transfer insentif ke rekening ini setelah verifikasi foto bukti (dengan timestamp).'}
            </p>
            <div className="space-y-2">
              <Input placeholder={lang === 'en' ? 'Bank Name * (e.g. BCA, Mandiri)' : 'Nama Bank * (mis. BCA, Mandiri)'} value={f.bank_name} onChange={e => setF({ ...f, bank_name: e.target.value })} data-testid="meet-input-bank-name" />
              <Input placeholder={lang === 'en' ? 'Account Holder Name *' : 'Nama Pemilik Rekening *'} value={f.bank_account_holder} onChange={e => setF({ ...f, bank_account_holder: e.target.value })} data-testid="meet-input-bank-holder" />
              <Input placeholder={lang === 'en' ? 'Account Number *' : 'Nomor Rekening *'} value={f.bank_account_number} onChange={e => setF({ ...f, bank_account_number: e.target.value.replace(/[^0-9-]/g, '') })} data-testid="meet-input-bank-number" inputMode="numeric" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{lang === 'en' ? 'Cancel' : 'Batal'}</Button>
          <Button onClick={submit} disabled={loading} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="meet-submit-btn">{loading ? '...' : (lang === 'en' ? 'Create' : 'Buat')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MeetDetailModal({ API, authHeader, lang, scope = 'buddhist', meet, member, onClose, onChange }) {
  const [uploading, setUploading] = useState(false);
  const isInitiator = meet.initiator_id === member?.id;
  const isRegistered = (meet.participants || []).some(p => p.account_id === member?.id);
  const canRegister = !isRegistered && !isInitiator && (meet.participants?.length || 0) < meet.max_participants;

  const register = async () => {
    try {
      await axios.post(`${API}/${scope}/meets/${meet.id}/register`, {}, { headers: authHeader });
      toast.success(lang === 'en' ? 'Registered!' : 'Terdaftar!');
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/${scope}/meets/${meet.id}/upload-proof`, fd, { headers: { ...authHeader, 'Content-Type': 'multipart/form-data' } });
      toast.success(lang === 'en' ? 'Proof uploaded' : 'Bukti terunggah');
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setUploading(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-card t-text max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{meet.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2 text-sm">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#D98A94]/15 text-[#D98A94]">{meet.event_category}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-black/5 t-text-muted">{meet.event_date}{meet.event_time_start ? ` · ${meet.event_time_start}${meet.event_time_end ? `–${meet.event_time_end}` : ''}` : ''}</span>
          </div>
          <div><span className="t-text-muted">📍 </span>{meet.location}{meet.maps_url ? <> · <a href={meet.maps_url} target="_blank" rel="noreferrer" className="text-[#D98A94] underline text-xs">Maps</a></> : null}</div>
          {meet.description && <p className="t-text-secondary whitespace-pre-line">{meet.description}</p>}
          <div className="flex justify-between rounded-lg glass-card p-3">
            <div><div className="text-xs t-text-muted">{lang === 'en' ? 'Registered' : 'Terdaftar'}</div><div className="font-bold t-text">{meet.participants?.length || 0} / {meet.max_participants}</div></div>
            <div><div className="text-xs t-text-muted">{lang === 'en' ? 'Incentive' : 'Insentif'}</div><div className="font-bold text-[#D98A94]">{rp(incentiveFor(meet.participants?.length || 0))}</div></div>
          </div>
          <div className="text-xs t-text-muted">
            {lang === 'en' ? 'Initiator' : 'Inisiator'}: <b className="t-text">{meet.initiator_name}</b>
          </div>
          {canRegister && (
            <Button onClick={register} data-testid="meet-register-btn" className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full">{lang === 'en' ? 'Register' : 'Daftar'}</Button>
          )}
          {isRegistered && !isInitiator && <div className="text-center text-xs text-green-600">✓ {lang === 'en' ? 'You are registered' : 'Anda sudah terdaftar'}</div>}
          {isInitiator && meet.status !== 'paid' && (
            <div className="rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.30)' }}>
              <div className="font-semibold text-xs t-text mb-1"><Camera className="w-3 h-3 inline mr-1" />{lang === 'en' ? 'Proof Photo (with camera timestamp)' : 'Bukti Foto (dengan timestamp kamera)'}</div>
              {meet.proof_photo_path ? (
                <div className="text-xs text-green-600">✓ {lang === 'en' ? 'Proof uploaded' : 'Bukti sudah terunggah'} · {meet.proof_participant_count} {lang === 'en' ? 'attendees' : 'peserta'} · {rp(meet.incentive_amount)}</div>
              ) : (
                <>
                  <p className="text-xs t-text-muted mb-2">{lang === 'en' ? 'Take a photo from your camera app so the EXIF timestamp is preserved.' : 'Ambil foto langsung dari kamera agar timestamp EXIF tersimpan.'}</p>
                  <input type="file" accept="image/*" capture="environment" onChange={uploadProof} disabled={uploading} data-testid="meet-proof-input" className="text-xs" />
                </>
              )}
            </div>
          )}
          {meet.incentive_paid && <div className="text-center text-sm text-green-600 font-semibold">✓ {lang === 'en' ? 'Incentive paid' : 'Insentif sudah dibayar'} · {rp(meet.incentive_amount)}</div>}
        </div>
        <Button onClick={onClose} variant="outline" className="w-full mt-4 rounded-full">{lang === 'en' ? 'Close' : 'Tutup'}</Button>
      </DialogContent>
    </Dialog>
  );
}

// ============ MART ============
function MartSection({ API, authHeader, lang, member, scope = "buddhist" }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all/sell/job/share
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? `${API}/${scope}/mart` : `${API}/${scope}/mart?listing_type=${filter}`;
      const { data } = await axios.get(url, { headers: authHeader });
      setListings(data.listings || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  }, [API, authHeader, filter]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm(lang === 'en' ? 'Delete this listing?' : 'Hapus listing ini?')) return;
    try { await axios.delete(`${API}/${scope}/mart/${id}`, { headers: authHeader }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-1">
          {[
            ['all', lang === 'en' ? 'All' : 'Semua'],
            ['sell', lang === 'en' ? 'Sell' : 'Jual'],
            ['buy', lang === 'en' ? 'Want to Buy' : 'Beli (WTB)'],
            ['job', lang === 'en' ? 'Jobs' : 'Lowongan'],
            ['share', lang === 'en' ? 'Free/Share' : 'Bagi Gratis'],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`mart-filter-${k}`} className={`px-3 py-1.5 rounded-full text-xs ${filter === k ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}>{l}</button>
          ))}
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="mart-create-btn" className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full text-sm"><Plus className="w-4 h-4 mr-1" />{lang === 'en' ? 'Post' : 'Posting'}</Button>
      </div>

      {loading ? <div className="t-text-muted text-sm text-center py-8">Loading...</div> : listings.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center"><ShoppingBag className="w-10 h-10 text-[#D98A94] mx-auto mb-3" /><p className="t-text-muted text-sm">{lang === 'en' ? 'No listings yet' : 'Belum ada listing'}</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {listings.map(l => {
            const typeLabel = l.listing_type === 'job'
              ? (lang === 'en' ? 'Job' : 'Lowongan')
              : l.listing_type === 'share'
                ? (lang === 'en' ? 'Free' : 'Gratis')
                : l.listing_type === 'buy'
                  ? (lang === 'en' ? 'Want to Buy' : 'Dicari')
                  : (lang === 'en' ? 'For Sale' : 'Dijual');
            const typeColor = l.listing_type === 'job'
              ? '#2563EB'
              : l.listing_type === 'share'
                ? '#16A34A'
                : l.listing_type === 'buy'
                  ? '#F59E0B'
                  : '#D98A94';
            const isMine = l.poster_id === member?.id;
            return (
              <div key={l.id} className="glass-card rounded-2xl p-4" data-testid={`mart-card-${l.id}`}>
                {l.photos?.[0] && <img src={l.photos[0]} alt="" className="w-full aspect-square object-cover rounded-lg mb-2" />}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-semibold t-text text-sm line-clamp-2">{l.title}</h3>
                  {isMine && <button onClick={() => del(l.id)} data-testid={`mart-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5 t-text-muted hover:text-red-500" /></button>}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${typeColor}20`, color: typeColor }}>{typeLabel}</span>
                  <span className="text-[10px] t-text-muted">{l.category}</span>
                </div>
                {l.price && <div className="text-sm font-bold text-[#D98A94] mb-1">{l.price}</div>}
                {l.description && <p className="text-xs t-text-secondary line-clamp-2 mb-2">{l.description}</p>}
                <div className="text-[10px] t-text-muted mb-2">{l.poster_name}</div>
                {l.poster_whatsapp && (
                  <a href={`https://wa.me/${l.poster_whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Halo, saya tertarik dengan: ' + l.title)}`} target="_blank" rel="noreferrer"
                    className="block text-center bg-[#25D366] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#25D366]/90" data-testid={`mart-contact-${l.id}`}>
                    {l.listing_type === 'job'
                      ? (lang === 'en' ? 'Apply via WhatsApp' : 'Apply via WhatsApp')
                      : l.listing_type === 'buy'
                        ? (lang === 'en' ? 'Offer via WhatsApp' : 'Tawarkan via WhatsApp')
                        : (lang === 'en' ? 'Contact via WhatsApp' : 'Hubungi via WhatsApp')}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
      {createOpen && <MartCreateModal API={API} authHeader={authHeader} lang={lang} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

function MartCreateModal({ API, authHeader, lang, onClose, onCreated, scope = "buddhist" }) {
  const [f, setF] = useState({ listing_type: 'sell', title: '', description: '', price: '', category: 'Umum', photos: [] });
  const [loading, setLoading] = useState(false);
  const addPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error(lang === 'en' ? 'Max 2MB per photo' : 'Maks 2MB per foto');
    const r = new FileReader();
    r.onload = () => setF(prev => ({ ...prev, photos: [...prev.photos, r.result].slice(0, 5) }));
    r.readAsDataURL(file);
  };
  const submit = async () => {
    if (!f.title.trim()) return toast.error(lang === 'en' ? 'Title required' : 'Judul wajib');
    setLoading(true);
    try {
      await axios.post(`${API}/${scope}/mart`, f, { headers: authHeader });
      toast.success(lang === 'en' ? 'Posted' : 'Terposting');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-card t-text max-w-md">
        <DialogHeader><DialogTitle>{lang === 'en' ? 'New Post' : 'Post Baru'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <select value={f.listing_type} onChange={e => setF({ ...f, listing_type: e.target.value })} className="w-full h-10 rounded-lg t-border border px-3 t-text bg-transparent" data-testid="mart-input-type">
            <option value="sell">{lang === 'en' ? 'Sell (Marketplace)' : 'Jual (Marketplace)'}</option>
            <option value="buy">{lang === 'en' ? 'Want to Buy (WTB)' : 'Beli / Dicari (WTB)'}</option>
            <option value="job">{lang === 'en' ? 'Job Listing' : 'Lowongan Kerja'}</option>
            <option value="share">{lang === 'en' ? 'Free / Share' : 'Bagi Gratis'}</option>
          </select>
          <Input placeholder={lang === 'en' ? 'Title *' : 'Judul *'} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="mart-input-title" />
          <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className="w-full h-10 rounded-lg t-border border px-3 t-text bg-transparent" data-testid="mart-input-category">
            {MART_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input
            placeholder={
              f.listing_type === 'job'
                ? (lang === 'en' ? 'Salary (e.g. 5-8 juta/mo)' : 'Gaji (mis. 5-8 juta/bln)')
                : f.listing_type === 'buy'
                  ? (lang === 'en' ? 'Your Budget (e.g. Rp 5jt / Nego)' : 'Budget Anda (mis. Rp 5jt / Nego)')
                  : (lang === 'en' ? 'Price (e.g. Rp 500rb / Nego)' : 'Harga (mis. Rp 500rb / Nego)')
            }
            value={f.price}
            onChange={e => setF({ ...f, price: e.target.value })}
            data-testid="mart-input-price"
          />
          <Textarea placeholder={lang === 'en' ? 'Description' : 'Deskripsi'} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="mart-input-desc" />
          <div>
            <label className="text-xs t-text-muted">{lang === 'en' ? 'Photos (max 5, ≤2MB each)' : 'Foto (maks 5, ≤2MB per foto)'}</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {f.photos.map((p, i) => (
                <div key={p} className="relative"><img src={p} alt="" className="w-16 h-16 object-cover rounded" /><button onClick={() => setF({ ...f, photos: f.photos.filter((_, j) => j !== i) })} className="absolute -top-1 -right-1 bg-red-500 text-white w-4 h-4 rounded-full text-[10px]"><X className="w-3 h-3 mx-auto" /></button></div>
              ))}
              {f.photos.length < 5 && <label className="w-16 h-16 border-2 border-dashed t-border rounded flex items-center justify-center cursor-pointer"><Upload className="w-4 h-4 t-text-muted" /><input type="file" accept="image/*" onChange={addPhoto} className="hidden" data-testid="mart-input-photo" /></label>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{lang === 'en' ? 'Cancel' : 'Batal'}</Button>
          <Button onClick={submit} disabled={loading} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="mart-submit-btn">{loading ? '...' : (lang === 'en' ? 'Post' : 'Posting')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MASTERY ============
const RESOURCES_PER_PAGE = 6;

export function MasterySection({ API, token, authHeader, accountId, lang, scope = "buddhist" }) {
  const [subtab, setSubtab] = useState('resources'); // resources | qa
  const [resources, setResources] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const isID = lang !== 'en';

  // Reset to page 1 whenever the query changes
  useEffect(() => { setPage(1); }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, q] = await Promise.all([
        axios.get(`${API}/${scope}/mastery/resources`, { headers: authHeader }),
        axios.get(`${API}/${scope}/mastery/qa`, { headers: authHeader }),
      ]);
      setResources(r.data.resources || []);
      setThreads(q.data.threads || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  }, [API, authHeader]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setSubtab('resources')} data-testid="mastery-tab-resources" className={`flex-1 py-2 rounded-lg text-sm font-medium ${subtab === 'resources' ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}><BookOpen className="w-4 h-4 inline mr-1" />{lang === 'en' ? 'Library' : 'Pustaka'}</button>
        <button onClick={() => setSubtab('qa')} data-testid="mastery-tab-qa" className={`flex-1 py-2 rounded-lg text-sm font-medium ${subtab === 'qa' ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}><MessageSquare className="w-4 h-4 inline mr-1" />{lang === 'en' ? 'Q&A Forum' : 'Forum Q&A'}</button>
      </div>

      {subtab === 'resources' ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#D98A94]" />
                <h3 className="text-sm font-bold t-text uppercase tracking-widest">{isID ? 'Pustaka' : 'Library'}</h3>
              </div>
              <p className="text-[11px] t-text-muted mt-0.5">
                {isID ? 'Kumpulan materi belajar komunitas — PDF, audio, video, & YouTube.' : 'Community learning materials — PDF, audio, video, & YouTube.'}
              </p>
            </div>
            <Button onClick={() => setUploadOpen(true)} data-testid="mastery-upload-btn" size="sm" className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-9 px-4">
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              <span className="text-xs font-semibold">{isID ? 'Upload Materi' : 'Upload Material'}</span>
            </Button>
          </div>

          <div className="relative mb-4">
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              data-testid="mastery-search"
              placeholder={isID ? 'Cari materi… (judul, deskripsi, tag, uploader)' : 'Search library…'}
              className="w-full h-11 pl-10 pr-4 rounded-full bg-white border t-border t-text placeholder:t-text-muted text-sm outline-none focus:border-[#D98A94] focus:ring-2 focus:ring-[#D98A94]/20 transition"
            />
            <svg className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 t-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>
          </div>

          {loading ? <div className="t-text-muted text-sm text-center py-8">Loading...</div> :
            resources.length === 0 ? <div className="glass-card rounded-2xl p-10 text-center"><BookOpen className="w-10 h-10 text-[#D98A94] mx-auto mb-3" /><p className="t-text-muted text-sm">{lang === 'en' ? 'No resources yet. Be the first to upload!' : 'Belum ada materi. Jadilah yang pertama upload!'}</p></div> : (
              (() => {
                const q = query.trim().toLowerCase();
                const filtered = q
                  ? resources.filter(r => [r.title, r.description, r.uploader_name, r.original_filename, (r.tags || []).join(' ')].filter(Boolean).join(' ').toLowerCase().includes(q))
                  : resources;
                if (filtered.length === 0) return (
                  <div className="glass-card rounded-2xl p-8 text-center t-text-muted text-sm">
                    {isID ? 'Tidak ada hasil untuk pencarian ini.' : 'No results for this search.'}
                  </div>
                );
                const totalPages = Math.max(1, Math.ceil(filtered.length / RESOURCES_PER_PAGE));
                const safePage = Math.min(Math.max(1, page), totalPages);
                const startIdx = (safePage - 1) * RESOURCES_PER_PAGE;
                const pageItems = filtered.slice(startIdx, startIdx + RESOURCES_PER_PAGE);
                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] t-text-muted font-semibold">
                        <span className="t-text">{filtered.length}</span> {isID ? 'materi' : 'materials'}
                        {q ? <span> · {isID ? 'filter aktif' : 'filter active'}</span> : null}
                      </p>
                      <p className="text-[11px] t-text-muted font-semibold">
                        {isID ? 'Halaman' : 'Page'} <span className="t-text">{safePage}</span> {isID ? 'dari' : 'of'} <span className="t-text">{totalPages}</span>
                      </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mastery-resources-grid">
                      {pageItems.map(r => <ResourceCard key={r.id} resource={r} token={token} API={API} authHeader={authHeader} accountId={accountId} lang={lang} onDeleted={load} scope={scope} />)}
                    </div>
                    {totalPages > 1 && (
                      <MasteryPagination page={safePage} totalPages={totalPages} onChange={setPage} lang={lang} />
                    )}
                  </>
                );
              })()
            )}
          {uploadOpen && <MasteryUploadModal API={API} authHeader={authHeader} lang={lang} onClose={() => setUploadOpen(false)} onUploaded={() => { setUploadOpen(false); load(); }}  scope={scope} />}
        </>
      ) : (
        <div>
          <Button onClick={() => setAskOpen(true)} data-testid="mastery-ask-btn" className="w-full mb-3 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full"><Plus className="w-4 h-4 mr-1" />{lang === 'en' ? 'Ask a Question' : 'Ajukan Pertanyaan'}</Button>
          {threads.length === 0 ? <div className="glass-card rounded-2xl p-10 text-center"><MessageSquare className="w-10 h-10 text-[#D98A94] mx-auto mb-3" /><p className="t-text-muted text-sm">{lang === 'en' ? 'No questions yet' : 'Belum ada pertanyaan'}</p></div> : (
            <div className="space-y-3">
              {threads.map(t => <QaThread key={t.id} thread={t} API={API} authHeader={authHeader} lang={lang} onReplied={load}  scope={scope} />)}
            </div>
          )}
          {askOpen && <AskModal API={API} authHeader={authHeader} lang={lang} onClose={() => setAskOpen(false)} onAsked={() => { setAskOpen(false); load(); }}  scope={scope} />}
        </div>
      )}
    </div>
  );
}

function MasteryUploadModal({ scope = "buddhist",  API, authHeader, lang, onClose, onUploaded }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const isID = lang !== 'en';
  const submit = async () => {
    if (!title.trim() || !file) return toast.error(isID ? 'Judul & file wajib diisi' : 'Title & file are required');
    if (file.size > 300 * 1024 * 1024) return toast.error(isID ? 'File maksimal 300 MB' : 'Max 300 MB');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('description', description.trim());
      fd.append('tags', tags.trim());
      await axios.post(`${API}/${scope}/mastery/member-upload`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(isID ? 'Materi terunggah' : 'Uploaded');
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-card t-text max-w-md">
        <DialogHeader><DialogTitle>{isID ? 'Upload Materi Pustaka' : 'Upload Library Material'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <Input placeholder={isID ? 'Judul *' : 'Title *'} value={title} onChange={e => setTitle(e.target.value)} data-testid="mastery-upload-title" />
          <Textarea placeholder={isID ? 'Deskripsi singkat' : 'Short description'} value={description} onChange={e => setDescription(e.target.value)} data-testid="mastery-upload-desc" />
          <Input placeholder={isID ? 'Tag (pisah dengan koma)' : 'Tags (comma-separated)'} value={tags} onChange={e => setTags(e.target.value)} data-testid="mastery-upload-tags" />
          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">
              {isID ? 'File (PDF, DOC, PPT, Audio, Video, Gambar) — max 300 MB' : 'File (PDF, DOC, PPT, Audio, Video, Image) — max 300 MB'} *
            </label>
            <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.epub,.mp3,.m4a,.wav,.ogg,.aac,.mp4,.mov,.webm,.m4v,.jpg,.jpeg,.png,.webp"
              onChange={e => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="mastery-upload-file" />
            {file && <div className="text-[10px] t-text-muted mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>}
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{isID ? 'Batal' : 'Cancel'}</Button>
          <Button onClick={submit} disabled={busy} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="mastery-upload-submit">
            {busy ? '...' : (isID ? 'Upload' : 'Upload')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResourceCard({ resource, token, API, authHeader, accountId, lang, onDeleted, scope = "buddhist" }) {
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pptOpen, setPptOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isID = lang !== 'en';

  const isOwn = !!accountId && resource.uploader_id === accountId;

  const del = async () => {
    if (!window.confirm(isID ? 'Hapus materi ini secara permanen?' : 'Delete this material permanently?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/${scope}/mastery/${resource.id}`, { headers: authHeader });
      toast.success(isID ? 'Materi dihapus' : 'Deleted');
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setDeleting(false); }
  };

  if (resource.resource_type === 'youtube') {
    const meta = [resource.uploader_name, resource.created_at && new Date(resource.created_at).toLocaleString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })]
      .filter(Boolean).join(' · ');
    return (
      <div className="glass-card rounded-2xl overflow-hidden" data-testid={`resource-yt-${resource.id}`}>
        <div className="aspect-video">
          <iframe src={`https://www.youtube.com/embed/${resource.youtube_id}`} title={resource.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
        <div className="p-3">
          <div className="flex items-center gap-1 mb-1"><Youtube className="w-3 h-3 text-red-500" /><span className="text-[10px] t-text-muted">YouTube</span></div>
          <h3 className="font-semibold t-text text-sm">{resource.title}</h3>
          {resource.description && <p className="text-xs t-text-secondary mt-1">{resource.description}</p>}
          {meta && (
            <p className="text-[10px] t-text-muted mt-2 italic">
              {isID ? 'Ditambahkan oleh' : 'Added by'}: {meta}
            </p>
          )}
          {isOwn && (
            <button onClick={del} disabled={deleting} data-testid={`resource-delete-${resource.id}`} className="mt-2 text-[10px] text-red-500 hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" />{isID ? 'Hapus' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    );
  }
  const ct = resource.content_type || '';
  const ext = (resource.original_filename || '').split('.').pop().toLowerCase();
  const isImage = ct.startsWith('image/');
  const isPdf = ct === 'application/pdf';
  const isAudio = ct.startsWith('audio/');
  const isVideo = ct.startsWith('video/');
  const isPpt = ['ppt', 'pptx'].includes(ext) || ct.includes('powerpoint') || ct.includes('presentation');
  const isDoc = !isPpt && (['doc', 'docx', 'xls', 'xlsx'].includes(ext) || ct.includes('word') || ct.includes('excel') || ct.includes('spreadsheet'));
  const url = `${API}/${scope}/files/${resource.storage_path}?auth=${encodeURIComponent(token)}`;
  // Office Online viewer needs an absolute, publicly-fetchable URL.
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

  return (
    <div className="glass-card rounded-2xl p-4" data-testid={`resource-file-${resource.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-[#D98A94] shrink-0" />
          <span className="text-[10px] t-text-muted uppercase">{ext}</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          {resource.uploader_name && <span className="text-[10px] t-text-muted truncate max-w-[40%]">by {resource.uploader_name}</span>}
          {isOwn && (
            <button onClick={del} disabled={deleting} data-testid={`resource-delete-${resource.id}`} title={isID ? 'Hapus' : 'Delete'} className="p-1 rounded hover:bg-red-500/10 text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <h3 className="font-semibold t-text text-sm mb-1">{resource.title}</h3>
      {resource.description && <p className="text-xs t-text-secondary mb-3 line-clamp-2">{resource.description}</p>}
      {isImage && <img src={url} alt={resource.title} className="w-full rounded-lg mb-2" />}
      {isAudio && (
        <audio controls preload="metadata" className="w-full mb-2" data-testid={`resource-audio-${resource.id}`}>
          <source src={url} type={ct} />
        </audio>
      )}
      {isVideo && (
        <video controls preload="metadata" className="w-full rounded-lg mb-2" data-testid={`resource-video-${resource.id}`}>
          <source src={url} type={ct} />
        </video>
      )}
      {isPdf && (
        <>
          <div className="bg-gray-100 rounded-lg overflow-hidden mb-2" style={{ height: 360 }}>
            <iframe
              src={`${url}#toolbar=1&navpanes=0&view=FitH`}
              title={resource.title}
              className="w-full h-full border-0"
              data-testid={`resource-pdf-inline-${resource.id}`}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPdfOpen(true)} data-testid={`resource-pdf-read-${resource.id}`} className="flex-1 bg-[#D98A94] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#D98A94]/90">
              {isID ? 'Buka Layar Penuh' : 'Open Fullscreen'}
            </button>
            <a href={url} download={resource.original_filename || 'document.pdf'} className="glass-card border t-text-secondary text-xs font-semibold py-2 px-3 rounded-lg hover:border-[#D98A94]" data-testid={`resource-pdf-download-${resource.id}`}>
              {isID ? 'Unduh' : 'Download'}
            </a>
          </div>
          <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
            <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid={`resource-pdf-modal-${resource.id}`}>
              <DialogHeader className="px-5 py-3 border-b flex-row items-center justify-between space-y-0">
                <DialogTitle className="text-sm sm:text-base truncate">{resource.title}</DialogTitle>
                <a href={url} download={resource.original_filename || 'document.pdf'} className="text-xs font-semibold text-[#D98A94] hover:underline shrink-0 ml-3">
                  {isID ? '⇩ Unduh' : '⇩ Download'}
                </a>
              </DialogHeader>
              <div className="flex-1 bg-gray-100 overflow-hidden">
                <iframe src={`${url}#toolbar=1&navpanes=0&view=FitH`} title={resource.title} className="w-full h-full border-0 block" data-testid={`resource-pdf-iframe-${resource.id}`} />
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
      {isPpt && (
        <>
          <div className="bg-gray-100 rounded-lg overflow-hidden mb-2 relative" style={{ height: 360 }}>
            <iframe
              src={officeViewerUrl}
              title={resource.title}
              className="w-full h-full border-0 block"
              data-testid={`resource-ppt-inline-${resource.id}`}
              allow="fullscreen"
            />
            <div className="absolute bottom-1 right-2 text-[9px] t-text-muted italic bg-white/70 px-1.5 rounded">
              {isID ? 'Preview via Microsoft Office Online' : 'Preview via Microsoft Office Online'}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPptOpen(true)} data-testid={`resource-ppt-preview-${resource.id}`} className="flex-1 bg-[#D98A94] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#D98A94]/90">
              {isID ? 'Buka Layar Penuh' : 'Open Fullscreen'}
            </button>
            <a href={url} download={resource.original_filename || `slides.${ext}`} className="glass-card border t-text-secondary text-xs font-semibold py-2 px-3 rounded-lg hover:border-[#D98A94]" data-testid={`resource-ppt-download-${resource.id}`}>
              {isID ? 'Unduh' : 'Download'}
            </a>
          </div>
          <Dialog open={pptOpen} onOpenChange={setPptOpen}>
            <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid={`resource-ppt-modal-${resource.id}`}>
              <DialogHeader className="px-5 py-3 border-b flex-row items-center justify-between space-y-0">
                <DialogTitle className="text-sm sm:text-base truncate">{resource.title} · {ext.toUpperCase()}</DialogTitle>
                <a href={url} download={resource.original_filename || `slides.${ext}`} className="text-xs font-semibold text-[#D98A94] hover:underline shrink-0 ml-3">
                  {isID ? '⇩ Unduh' : '⇩ Download'}
                </a>
              </DialogHeader>
              <div className="flex-1 bg-gray-100 overflow-hidden relative">
                <iframe src={officeViewerUrl} title={resource.title} className="w-full h-full border-0 block" data-testid={`resource-ppt-iframe-${resource.id}`} allow="fullscreen" />
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
      {isDoc && (
        <a href={officeViewerUrl} target="_blank" rel="noreferrer" className="block text-center bg-[#D98A94] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#D98A94]/90" data-testid={`resource-doc-open-${resource.id}`}>
          {isID ? 'Buka Dokumen' : 'Open Document'}
        </a>
      )}
      {!isImage && !isPdf && !isAudio && !isVideo && !isPpt && !isDoc && (
        <a href={url} target="_blank" rel="noreferrer" className="block text-center bg-[#D98A94] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#D98A94]/90">
          {isID ? 'Buka / Unduh' : 'Open / Download'}
        </a>
      )}
      {(isImage || isAudio || isVideo) && (
        <a href={url} download={resource.original_filename || ''} className="block text-center text-[11px] t-text-muted hover:underline mt-1" data-testid={`resource-download-${resource.id}`}>
          ⇩ {isID ? 'Unduh file asli' : 'Download original'}
        </a>
      )}
      {(() => {
        const meta = [
          resource.uploader_name,
          resource.created_at && new Date(resource.created_at).toLocaleString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        ].filter(Boolean).join(' · ');
        return meta ? (
          <p className="text-[10px] t-text-muted mt-2 italic">
            {isID ? 'Ditambahkan oleh' : 'Added by'}: {meta}
          </p>
        ) : null;
      })()}
    </div>
  );
}

function AskModal({ scope = "buddhist",  API, authHeader, lang, onClose, onAsked }) {
  const [q, setQ] = useState(''); const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try { await axios.post(`${API}/${scope}/mastery/qa`, { question: q.trim() }, { headers: authHeader }); onAsked(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass-card t-text max-w-md">
        <DialogHeader><DialogTitle>{lang === 'en' ? 'Ask a Question' : 'Ajukan Pertanyaan'}</DialogTitle></DialogHeader>
        <Textarea placeholder={lang === 'en' ? 'What would you like to ask?' : 'Apa yang ingin Anda tanyakan?'} value={q} onChange={e => setQ(e.target.value)} rows={5} data-testid="ask-input" />
        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{lang === 'en' ? 'Cancel' : 'Batal'}</Button>
          <Button onClick={submit} disabled={loading || !q.trim()} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="ask-submit-btn">{loading ? '...' : (lang === 'en' ? 'Submit' : 'Kirim')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QaThread({ scope = "buddhist",  thread, API, authHeader, lang, onReplied }) {
  const [reply, setReply] = useState(''); const [open, setOpen] = useState(false); const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!reply.trim()) return;
    setLoading(true);
    try { await axios.post(`${API}/${scope}/mastery/qa/${thread.id}/reply`, { reply: reply.trim() }, { headers: authHeader }); setReply(''); onReplied(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <div className="glass-card rounded-2xl p-4" data-testid={`qa-thread-${thread.id}`}>
      <div className="flex justify-between items-start mb-2">
        <div><div className="text-xs t-text-muted">{thread.author_name}</div><p className="t-text text-sm mt-1">{thread.question}</p></div>
        <button onClick={() => setOpen(!open)} className="text-xs text-[#D98A94]">{thread.replies?.length || 0} {lang === 'en' ? 'replies' : 'balasan'}</button>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t t-border space-y-2">
          {(thread.replies || []).map(r => <div key={r.id} className="text-xs"><b className="t-text">{r.author_name}:</b> <span className="t-text-secondary">{r.reply}</span></div>)}
          <div className="flex gap-2">
            <Input placeholder={lang === 'en' ? 'Your reply...' : 'Balasan Anda...'} value={reply} onChange={e => setReply(e.target.value)} data-testid={`qa-reply-${thread.id}`} className="text-xs" />
            <Button onClick={submit} disabled={loading} className="bg-[#D98A94] text-white text-xs rounded-full" data-testid={`qa-reply-submit-${thread.id}`}>{lang === 'en' ? 'Send' : 'Kirim'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MEDIA ============
export function MediaSection({ API, token, authHeader, accountId, lang, scope = "buddhist" }) {
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // event being edited (own event)
  const [selectedDate, setSelectedDate] = useState(null);
  const isID = lang !== 'en';

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/${scope}/media`, { headers: authHeader });
      setSaved(data.events || []);
    } catch (e) { console.warn(e); }
  }, [API, authHeader]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return toast.error(isID ? 'Tempel teks broadcast dulu' : 'Paste broadcast text first');
    if (imageFile && imageFile.size > 15 * 1024 * 1024) {
      return toast.error(isID ? 'Gambar maksimal 15 MB' : 'Image max 15 MB');
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('text', text);
      if (imageFile) fd.append('image', imageFile);
      await axios.post(`${API}/${scope}/media/submit`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(isID ? 'Pengumuman tersimpan' : 'Announcement saved');
      setText(''); setImageFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const del = async (id) => {
    if (!window.confirm(isID ? 'Hapus pengumuman ini?' : 'Delete this announcement?')) return;
    try { await axios.delete(`${API}/${scope}/media/${id}`, { headers: authHeader }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="glass-card rounded-2xl p-4 mb-4">
        <h3 className="font-semibold t-text text-sm mb-2 flex items-center gap-1"><Megaphone className="w-4 h-4 text-[#D98A94]" />{isID ? 'Tempel Broadcast Vihara' : 'Paste Vihara Broadcast'}</h3>
        <Textarea
          rows={6}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={isID ? 'Tempel pesan broadcast lengkap di sini...\n\nTip: Pertahankan baris & spasi asli — akan tampil rapi setelah dikirim.' : 'Paste the full broadcast message here...\n\nTip: Keep original line breaks — they will be preserved after submit.'}
          data-testid="media-input"
        />
        <div className="mt-2 flex flex-col gap-2">
          <label className="flex items-center gap-2 text-xs t-text-muted cursor-pointer">
            <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="hidden" data-testid="media-image-input" />
            <span className="glass-card border border-[#D98A94]/40 rounded-full px-3 py-1.5 hover:bg-[#D98A94]/10 transition inline-flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5" />{imageFile ? imageFile.name.slice(0, 30) : (isID ? 'Tambahkan Gambar (opsional, maks 15 MB)' : 'Add Image (optional, max 15 MB)')}
            </span>
            {imageFile && <button onClick={() => setImageFile(null)} className="text-red-500 text-xs" type="button">×</button>}
          </label>
          <Button onClick={submit} disabled={loading || !text.trim()} className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="media-save-btn">
            {loading ? '...' : (isID ? 'Kirim Pengumuman' : 'Submit Announcement')}
          </Button>
        </div>
      </div>

      <MediaCalendarWidget
        events={saved}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        lang={lang}
      />

      <h3 className="font-semibold t-text text-sm mb-3 mt-4 flex items-center justify-between gap-2">
        <span>{isID ? 'Pengumuman Acara' : 'Event Announcements'}</span>
        {selectedDate && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D98A94]/10 text-[#C0727C]" data-testid="media-selected-date">
            {isID ? 'Filter' : 'Filter'}: {new Date(selectedDate).toLocaleDateString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'long', year: 'numeric' })}
          </span>
        )}
      </h3>
      {(() => {
        const filtered = selectedDate ? saved.filter(e => e.date_iso === selectedDate) : saved;
        if (filtered.length === 0) {
          return (
            <div className="glass-card rounded-2xl p-6 text-center text-sm t-text-muted">
              {selectedDate
                ? (isID ? 'Tidak ada acara pada tanggal ini.' : 'No events on this date.')
                : (isID ? 'Belum ada pengumuman' : 'No announcements yet')}
            </div>
          );
        }
        return (
          <div className="space-y-3">
            {filtered.map(e => (
              <AnnouncementCard
                key={e.id}
                event={e}
                API={API}
                token={token}
                authHeader={authHeader}
                accountId={accountId}
                lang={lang}
                onEdit={() => setEditing(e)}
                onDelete={() => del(e.id)} scope={scope}
              />
            ))}
          </div>
        );
      })()}

      {editing && (
        <MediaEditModal
          event={editing}
          API={API}
          authHeader={authHeader}
          lang={lang}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
         scope={scope} />
      )}
    </div>
  );
}

function AnnouncementCard({ event, API, token, authHeader: _authHeader, accountId, lang, onEdit, onDelete, scope = "buddhist" }) {
  const isID = lang !== 'en';
  const isOwn = event.submitter_id === accountId;
  const imgUrl = event.image_url ? `${API}/${scope}/files/${event.image_url}?auth=${encodeURIComponent(token)}` : '';
  const created = event.created_at ? new Date(event.created_at) : null;
  const dateStr = created && !isNaN(created.getTime())
    ? created.toLocaleDateString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return (
    <article className="glass-card rounded-2xl overflow-hidden" data-testid={`media-event-${event.id}`}>
      {imgUrl && (
        <div className="w-full bg-black/5">
          <img src={imgUrl} alt={event.event_type || 'announcement'} className="w-full max-h-[420px] object-contain block" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        {/* Meta chips */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#D98A94]/15 text-[#D98A94]">{event.event_type}</span>
          {event.city && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/5 t-text-muted inline-flex items-center gap-0.5">
              <MapPin className="w-2.5 h-2.5" />{event.city}
            </span>
          )}
          {event.date_display && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/5 t-text-muted inline-flex items-center gap-0.5">
              <Calendar className="w-2.5 h-2.5" />{event.date_display}
            </span>
          )}
          {event.time_display && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/5 t-text-muted">🕘 {event.time_display}</span>
          )}
        </div>

        {/* Body: preserve broadcast formatting */}
        <div
          className="text-[13px] t-text-secondary leading-relaxed whitespace-pre-wrap break-words"
          style={{ wordBreak: 'break-word' }}
        >
          {event.original_text}
        </div>

        {/* Footer: submitter + actions */}
        <div className="mt-3 pt-3 flex items-center gap-2 text-[11px] t-text-muted border-t t-border">
          <span className="truncate flex-1">
            {isID ? 'Diunggah oleh' : 'Uploaded by'} <b className="t-text-secondary">{event.submitter_name || 'Anonim'}</b>
            {dateStr ? ` · ${dateStr}` : ''}
            {event.updated_at ? ` · ${isID ? 'diedit' : 'edited'}` : ''}
          </span>
          {isOwn && (
            <>
              <button onClick={onEdit} data-testid={`media-edit-${event.id}`} className="p-1 rounded-full hover:bg-[#D98A94]/10 text-[#D98A94]" title={isID ? 'Edit' : 'Edit'}>
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={onDelete} data-testid={`media-delete-${event.id}`} className="p-1 rounded-full hover:bg-red-500/10 text-red-500" title={isID ? 'Hapus' : 'Delete'}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function MediaEditModal({ scope = "buddhist",  event, API, authHeader, lang, onClose, onSaved }) {
  const isID = lang !== 'en';
  const [text, setText] = useState(event.original_text || '');
  const [imageFile, setImageFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasExistingImage = !!event.image_url && !removeImage;

  const submit = async () => {
    if (!text.trim()) return toast.error(isID ? 'Teks broadcast wajib diisi' : 'Text required');
    if (imageFile && imageFile.size > 15 * 1024 * 1024) {
      return toast.error(isID ? 'Gambar maksimal 15 MB' : 'Image max 15 MB');
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (text !== event.original_text) fd.append('text', text);
      if (imageFile) fd.append('image', imageFile);
      if (removeImage) fd.append('remove_image', '1');
      await axios.patch(`${API}/${scope}/media/${event.id}`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(isID ? 'Pengumuman diperbarui' : 'Announcement updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass-card t-text max-w-lg max-h-[90vh] overflow-y-auto" data-testid="media-edit-modal">
        <DialogHeader><DialogTitle>{isID ? 'Edit Pengumuman' : 'Edit Announcement'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{isID ? 'Teks broadcast' : 'Broadcast text'} *</label>
            <Textarea rows={8} value={text} onChange={e => setText(e.target.value)} data-testid="media-edit-text" />
            <div className="text-[10px] t-text-muted mt-1">
              {isID ? 'Baris baru & spasi akan dipertahankan setelah kirim.' : 'Line breaks & spacing will be preserved after submit.'}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wide t-text-muted block mb-1">{isID ? 'Gambar' : 'Image'}</label>
            {hasExistingImage && !imageFile && (
              <div className="mb-2 flex items-center gap-2 rounded-lg p-2" style={{ background: 'rgba(217,138,148,0.06)' }}>
                <ImageIcon className="w-3.5 h-3.5 text-[#D98A94]" />
                <span className="text-xs t-text-muted flex-1 truncate">{isID ? 'Gambar saat ini terpasang' : 'Current image attached'}</span>
                <button onClick={() => setRemoveImage(true)} className="text-xs text-red-500" data-testid="media-edit-remove-image">
                  {isID ? 'Hapus' : 'Remove'}
                </button>
              </div>
            )}
            {removeImage && !imageFile && (
              <div className="mb-2 flex items-center gap-2 rounded-lg p-2 border border-red-300/40 text-red-500">
                <span className="text-xs flex-1">{isID ? 'Gambar akan dihapus saat simpan' : 'Image will be removed on save'}</span>
                <button onClick={() => setRemoveImage(false)} className="text-xs t-text-muted underline" type="button">
                  {isID ? 'Batal' : 'Undo'}
                </button>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs t-text-muted cursor-pointer">
              <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="hidden" data-testid="media-edit-image-input" />
              <span className="glass-card border border-[#D98A94]/40 rounded-full px-3 py-1.5 hover:bg-[#D98A94]/10 transition inline-flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" />
                {imageFile ? imageFile.name.slice(0, 30) : (isID
                  ? (hasExistingImage ? 'Ganti gambar (opsional, maks 15 MB)' : 'Tambah gambar (opsional, maks 15 MB)')
                  : (hasExistingImage ? 'Replace image (optional, max 15 MB)' : 'Add image (optional, max 15 MB)'))}
              </span>
              {imageFile && <button onClick={() => setImageFile(null)} className="text-red-500 text-xs" type="button">×</button>}
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">{isID ? 'Batal' : 'Cancel'}</Button>
          <Button onClick={submit} disabled={busy} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="media-edit-submit">
            {busy ? '...' : (isID ? 'Simpan Perubahan' : 'Save Changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============ MASTERY PAGINATION ============
function MasteryPagination({ page, totalPages, onChange, lang }) {
  const isID = lang !== 'en';

  // Build compact page number list: 1 … (p-1) p (p+1) … totalPages
  const pages = [];
  const push = (v) => pages.push(v);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) push(i);
  } else {
    push(1);
    if (page > 3) push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) push(i);
    if (page < totalPages - 2) push('…');
    push(totalPages);
  }

  return (
    <nav
      role="navigation"
      aria-label={isID ? 'Navigasi halaman' : 'Pagination'}
      className="flex items-center justify-center gap-1.5 mt-5 flex-wrap"
      data-testid="mastery-pagination"
    >
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        data-testid="mastery-pagination-prev"
        className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white border t-border t-text-secondary text-xs font-semibold hover:border-[#D98A94] hover:text-[#D98A94] disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
        <span className="hidden sm:inline">{isID ? 'Sebelumnya' : 'Prev'}</span>
      </button>

      {pages.map((p, i) => (
        p === '…' ? (
          <span key={`gap-${i}`} className="w-9 h-9 flex items-center justify-center t-text-muted text-xs">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            data-testid={`mastery-pagination-page-${p}`}
            aria-current={p === page ? 'page' : undefined}
            className={`w-9 h-9 rounded-full text-xs font-bold transition-colors ${
              p === page
                ? 'bg-[#D98A94] text-white shadow-sm'
                : 'bg-white border t-border t-text-secondary hover:border-[#D98A94] hover:text-[#D98A94]'
            }`}
          >{p}</button>
        )
      ))}

      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        data-testid="mastery-pagination-next"
        className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white border t-border t-text-secondary text-xs font-semibold hover:border-[#D98A94] hover:text-[#D98A94] disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <span className="hidden sm:inline">{isID ? 'Berikutnya' : 'Next'}</span>
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
      </button>
    </nav>
  );
}
